import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play, Volume2, VolumeX } from 'lucide-react';
import { fetchReels } from '../api/client';
import { absMediaUrl } from './ProductCard';
import { cdnSrc } from '../utils/cdnImage';
import { useLocale } from '../context/LocaleContext';

// Single autoplay-in-view reel tile with optional unmute toggle.
const ReelTile = ({ product, muted, onToggleMute, isFirst }) => {
  const videoRef = useRef(null);
  const [wantsPlay, setWantsPlay] = useState(false);
  const [broken, setBroken] = useState(false);
  const videoUrl = product.videos && product.videos[0];
  const posterUrl = cdnSrc(absMediaUrl(product.image), 480);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoUrl || broken) return;

    const tryPlay = () => {
      if (!wantsPlay) return;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    const onCanPlay = () => tryPlay();
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('loadeddata', onCanPlay);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setWantsPlay(true);
            if (!el.src) { el.src = absMediaUrl(videoUrl); el.load(); }
            tryPlay();
          } else {
            setWantsPlay(false);
            if (!el.paused) el.pause();
          }
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('loadeddata', onCanPlay);
      try { el.pause(); } catch {}
    };
  }, [videoUrl, wantsPlay, broken]);

  const showVideo = videoUrl && !broken;

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`trending-reel-${product.id}`}
      className="group relative shrink-0 w-[220px] md:w-[260px] aspect-[9/16] rounded-xl overflow-hidden bg-black snap-start"
    >
      {showVideo ? (
        <video
          ref={videoRef}
          poster={posterUrl}
          className="w-full h-full object-cover"
          muted={muted}
          loop
          playsInline
          preload="none"
          onError={() => setBroken(true)}
        />
      ) : (
        <img
          src={posterUrl}
          alt={product.name}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      )}
      {/* Gradient overlays for legibility */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/40" />
      {/* Play chip when out of view (poster showing) */}
      {showVideo && !wantsPlay && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
            <Play size={18} className="text-white ml-0.5" fill="white" />
          </div>
        </div>
      )}
      {/* Unmute toggle (only on first tile for cleanliness) */}
      {isFirst && showVideo && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); onToggleMute(); }}
          data-testid="trending-reel-mute-toggle"
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/80 backdrop-blur-md flex items-center justify-center text-white transition"
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
      )}
      {/* Product info */}
      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
        <div className="text-[10px] uppercase tracking-widest text-white/80">{product.collection}</div>
        <div className="font-bold text-sm mt-0.5 line-clamp-1">{product.name}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[15px] font-black text-[#39ff14]">
            {product.priceEUR ? `€${product.priceEUR.toFixed(2)}` : `₹${product.price}`}
          </span>
          {product.discount > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#22c55e] text-black">
              {product.discount}% OFF
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const TrendingReels = () => {
  const [reels, setReels] = useState([]);
  const [muted, setMuted] = useState(true);
  const scrollerRef = useRef(null);
  const { t } = useLocale();

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchReels(6);
        setReels(data.items || []);
      } catch (err) {
        console.warn('[TrendingReels] fetch failed:', err.message);
      }
    })();
  }, []);

  if (!reels || reels.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.75), behavior: 'smooth' });
  };

  return (
    <section
      data-testid="trending-reels"
      className="relative py-10 md:py-14 bg-gradient-to-b from-black to-neutral-900 overflow-hidden"
    >
      {/* Neon grid backdrop */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(57,255,20,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.4) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(circle at 50% 50%, black 30%, transparent 80%)'
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-4 md:mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.35em] text-[#39ff14] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39ff14] animate-pulse" /> Live now
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mt-1">
              TRENDING <span className="text-[#39ff14]">REELS</span>
            </h2>
            <p className="text-xs md:text-sm text-white/60 mt-1">
              See our latest drops in motion · tap any reel to shop
            </p>
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scrollBy(-1)}
              className="w-10 h-10 rounded-full border border-white/20 text-white hover:bg-white/10 flex items-center justify-center transition"
              data-testid="trending-reels-prev"
              aria-label="Scroll left"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-10 h-10 rounded-full border border-white/20 text-white hover:bg-white/10 flex items-center justify-center transition"
              data-testid="trending-reels-next"
              aria-label="Scroll right"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory pb-2 scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
        >
          {reels.map((p, idx) => (
            <ReelTile
              key={p.id}
              product={p}
              muted={muted}
              onToggleMute={() => setMuted((m) => !m)}
              isFirst={idx === 0}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrendingReels;
